import { Loader2, RefreshCw, Tags } from 'lucide-react';
import { useCallback, useState } from 'react';

import {
  ActionConfirmDialog,
  PendingAction,
} from './components/ActionConfirmDialog';
import { FiltersForm } from './components/FiltersForm';
import { MetricsCards } from './components/MetricsCards';
import { MonitorTable } from './components/MonitorTable';
import { getCurrentMonthScope } from './filtersSchema';

import { NoContentFallback } from '@/components/NoContentFallBack';
import { PaginationV2 } from '@/components/PaginationV2';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useGetVisionMonitorProducts } from '@/hooks/useGetVisionMonitorProducts';
import { useVisionMonitorActions } from '@/hooks/useVisionMonitorActions';
import {
  VisionMonitorFilter,
  VisionMonitorScope,
} from '@/services/VisionMonitorService';

const PAGE_SIZES = [25, 50, 100];

export function AdminVisionMonitor() {
  const [scope, setScope] = useState<VisionMonitorScope>(getCurrentMonthScope);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );

  const { rows, totalItems, totalPages, isLoading, isError, refetch } =
    useGetVisionMonitorProducts({ ...scope, page, limit });
  const { reread, regenerateCategory, isPending } = useVisionMonitorActions();

  const applyScope = useCallback((next: VisionMonitorScope) => {
    setScope(next);
    setPage(1);
    setSelected(new Set());
  }, []);

  // Metric shortcut: narrow the table to a slice, replacing any filter
  // already set on the same column.
  function handleQuickFilter(filter: VisionMonitorFilter) {
    const others = (scope.filters ?? []).filter(
      (item) => item.field !== filter.field,
    );

    applyScope({ ...scope, filters: [...others, filter] });
  }

  function changePage(next: number) {
    setPage(next);
    setSelected(new Set());
  }

  function toggleRow(catalogId: number) {
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(catalogId)) next.add(catalogId);
      return next;
    });
  }

  function togglePage(checked: boolean) {
    setSelected(
      checked ? new Set(rows.map((row) => row.catalogId)) : new Set(),
    );
  }

  async function confirmAction({ type, catalogIds }: PendingAction) {
    setPendingAction(null);

    try {
      await (type === 'reread'
        ? reread(catalogIds)
        : regenerateCategory(catalogIds));
      setSelected(new Set());
    } catch {
      // The mutation hook already surfaces the error toast.
    }
  }

  const selectedIds = [...selected];

  return (
    <Card className="min-h-screen rounded-lg shadow">
      <CardContent>
        <div className="mt-2 border-b bg-white">
          <div className="container mx-auto flex items-center justify-between px-4 py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Vision Monitor
              </h1>
              <p className="mt-1 text-gray-600">
                Follow how products are read by Vision: category name, vision
                read, catalog criteria and SEO.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw
                className={isLoading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
              />
              Refresh
            </Button>
          </div>
        </div>

        <div className="rounded-lg bg-gray-50">
          <div className="container mx-auto space-y-6 px-4 py-6">
            <MetricsCards scope={scope} onQuickFilter={handleQuickFilter} />

            <FiltersForm scope={scope} onApply={applyScope} />

            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-gray-600">
                {totalItems.toLocaleString()} row(s) · sorted by creation date
                {selectedIds.length > 0 && ` · ${selectedIds.length} selected`}
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedIds.length === 0 || isPending}
                  onClick={() =>
                    setPendingAction({
                      type: 'reread',
                      catalogIds: selectedIds,
                    })
                  }
                >
                  <RefreshCw className="h-4 w-4" />
                  Re-read selected
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedIds.length === 0 || isPending}
                  onClick={() =>
                    setPendingAction({
                      type: 'category',
                      catalogIds: selectedIds,
                    })
                  }
                >
                  <Tags className="h-4 w-4" />
                  Regenerate category for selected
                </Button>

                <Select
                  value={String(limit)}
                  onValueChange={(value) => {
                    setLimit(Number(value));
                    changePage(1);
                  }}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size} per page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isError && (
              <p className="text-sm text-red-600">
                Could not load products. Check the filters and try again.
              </p>
            )}

            {rows.length > 0 && (
              <MonitorTable
                rows={rows}
                selected={selected}
                disabled={isPending}
                onToggleRow={toggleRow}
                onTogglePage={togglePage}
                onReread={(catalogId) =>
                  setPendingAction({ type: 'reread', catalogIds: [catalogId] })
                }
                onRegenerateCategory={(catalogId) =>
                  setPendingAction({
                    type: 'category',
                    catalogIds: [catalogId],
                  })
                }
              />
            )}

            {isLoading && rows.length === 0 && (
              <div className="flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <NoContentFallback
                title="No products found"
                description="Nothing matches the selected dates, customer and filters."
              />
            )}

            <PaginationV2
              page={page}
              totalPages={totalPages}
              hasNextPage={page < totalPages}
              hasPreviousPage={page > 1}
              nextPage={() => changePage(page + 1)}
              previousPage={() => changePage(page - 1)}
            />
          </div>
        </div>
      </CardContent>

      <ActionConfirmDialog
        action={pendingAction}
        onCancel={() => setPendingAction(null)}
        onConfirm={confirmAction}
      />
    </Card>
  );
}
